import { LoginPage, ProtectedRoute, SecurityProvider } from '@dentalux/security'
import { createTheme, ThemeProvider } from '@material-ui/core/styles'
import * as Sentry from '@sentry/browser'
import moment from 'moment'
import 'moment/locale/de'
import React, { useEffect, useState } from 'react'
import { I18nextProvider } from 'react-i18next'
import { QueryClient, QueryClientProvider } from 'react-query'
import { HashRouter as Router, Route, Switch } from 'react-router-dom'
import { getSecurityEnv } from './config'
import * as i18n from './config/i18n'
import Main from './Main'
import { typography } from './theme/typography'

const theme = createTheme({
  typography,
  palette: {
    primary: {
      light: '#7e8c97',
      main: '#24A1A1',
      dark: '#10536C',
      contrastText: '#fff',
    },
    secondary: {
      light: '#556069',
      main: '#F6FBFB',
      dark: '#2f353a',
      // dark: will be calculated from palette.secondary.main,
      contrastText: '#000',
    },
    grey: {
      600: '#757575',
    },
    // Used by `getContrastText()` to maximize the contrast between
    // the background and the text.
    contrastThreshold: 3,
    // Used by the functions below to shift a color's luminance by approximately
    // two indexes within its tonal palette.
    // E.g., shift from Red 500 to Red 300 or Red 700.
    tonalOffset: 0.2,
  },
  overrides: {
    MuiTableRow: {
      hover: {
        '&:hover': {
          backgroundColor: '#E8F6FB !important',
          cursor: 'pointer',
        },
      },
    },
    MuiFab: {
      root: {
        backgroundColor: '#fff',
        color: '#20a8d8',
      },
    },
    MuiChip: {
      root: {
        borderRadius: '4px',
      },
    },
  },
})

const queryClient = new QueryClient({ defaultOptions: { queries: { refetchOnWindowFocus: false } } })

const App: React.FC = () => {
  const [loadingTranslations, setLoadingTranslations] = useState(true)

  //  Do not log local errorss
  if (!process.env.REACT_APP_IS_LOCAL) {
    Sentry.init({
      dsn: `${process.env.REACT_APP_SENTRY_DSN_KEY}`,
      environment: getSecurityEnv(),
    })
  }

  useEffect(() => {
    const language = localStorage.getItem('language')
    if (language) {
      moment.locale(language)
    }
  }, [])

  useEffect(() => {
    i18n.init().finally(() => {
      setLoadingTranslations(false)
    })
  }, [setLoadingTranslations])

  if (loadingTranslations) return <div />

  return (
    <ThemeProvider theme={theme}>
      <I18nextProvider i18n={i18n.default}>
        <QueryClientProvider client={queryClient}>
          <Router>
            <SecurityProvider
              config={{
                loginPath: '/login',
                successPath: '/',
                loading: 'Loading...',
                application: 'INSIGHT',
                env: getSecurityEnv(),
                defaultLanguage: i18n.default.language as 'en' | 'de',
                endpoint: process.env.REACT_APP_HADES_URI,
                enableImpersonation: true,
              }}
            >
              <Switch>
                <Route path="/login" exact>
                  <LoginPage title="Insight" />
                </Route>
                <ProtectedRoute path="/">
                  <Main />
                </ProtectedRoute>
              </Switch>
            </SecurityProvider>
          </Router>
        </QueryClientProvider>
      </I18nextProvider>
    </ThemeProvider>
  )
}

export default App
